'use client';

import React, { useEffect, useRef, useState } from 'react';
import { signIn, useSession } from 'next-auth/react';
import Link, { useRouter } from '@/core/i18n/navigation';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { invalidateClientSessionCache } from '@/core/api/auth-helper';
import { fetchAPI } from '@/core/api/client';
import { SocialAuthButtons } from '@/modules/auth/components/SocialAuthButtons';
import { MfaChallenge, MfaLoginStep, mfaChallengeFromError } from '@/modules/auth/components/TwoFactor';
import { BlockProps } from './types';
import { cn } from '@/core/utils/cn';
import { useSiteConfig } from '@/core/components/providers/SiteConfigProvider';
import { hasModule, isSiteMode } from '@/core/entitlements';
import { parseTaxId, type CompanyCheck } from '@/core/utils/tax-id';
import { isStaffRole } from '@/core/auth/staffRoles';

type AuthMode = 'login' | 'register' | 'reset';


const DEFAULT_AUTH_ELEMENT_ORDER = [
  'title',
  'account_type',
  'fields',
  'forgot_password',
  'submit_button',
  'social_auth',
  'switch_mode',
];

export function AuthBoxBlock({ block, currentStyles = {}, isAdmin, authMode }: BlockProps) {
  const settings = block.settings || {};
  // A website (no shop module) has no customer accounts: the box only signs in the site's own staff.
  const allowRegister = !isSiteMode(useSiteConfig());
  const mode: AuthMode = authMode === 'register' && !allowRegister ? 'login' : authMode || 'login';

  const [localMode, setLocalMode] = useState<AuthMode>(mode);
  useEffect(() => { setLocalMode(mode); }, [mode]);

  const cardBg = currentStyles.cardBg !== undefined && currentStyles.cardBg !== ''
    ? currentStyles.cardBg
    : (currentStyles.cardBg === '' ? 'transparent' : '#ffffff');
  const cardBorderColor = currentStyles.cardBorderColor || '#f1f5f9';
  const cardBorderWidth = currentStyles.cardBorderWidth !== undefined ? currentStyles.cardBorderWidth : 1;
  const cardBorderRadius = currentStyles.cardBorderRadius !== undefined ? `${currentStyles.cardBorderRadius}px` : '16px';
  const cardShadow = currentStyles.cardShadow || 'xl';
  const isFullWidth = currentStyles.fullWidth === true;
  const maxWidth = isFullWidth ? '100%' : (currentStyles.maxWidth ? `${currentStyles.maxWidth}px` : '448px');
  const alignment = currentStyles.alignment || 'center';

  const baseFontFamily = currentStyles.fontFamily || undefined;
  const titleColor = currentStyles.titleColor || '#171717';
  const titleFontFamily = currentStyles.titleFontFamily || baseFontFamily;
  const titleFontSize = currentStyles.titleFontSize ? `${currentStyles.titleFontSize}px` : '24px';
  const titleFontWeight = currentStyles.titleFontWeight || '800';
  const titleAlign = currentStyles.titleAlign || 'center';

  const inputBg = currentStyles.inputBg || '#ffffff';
  const inputTextColor = currentStyles.inputTextColor || '#1f2937';
  const inputBorderColor = currentStyles.inputBorderColor || '#e2e8f0';
  const inputBorderRadius = currentStyles.inputBorderRadius !== undefined ? `${currentStyles.inputBorderRadius}px` : '8px';
  const inputHeight = currentStyles.inputHeight ? `${currentStyles.inputHeight}px` : '40px';
  const labelColor = currentStyles.labelColor || '#374151';

  const btnBg = currentStyles.btnBg || '';
  const btnTextColor = currentStyles.btnTextColor || '#ffffff';
  const btnBorderRadius = currentStyles.btnBorderRadius !== undefined ? `${currentStyles.btnBorderRadius}px` : '8px';
  const btnHeight = currentStyles.btnHeight ? `${currentStyles.btnHeight}px` : '42px';
  const btnFontFamily = currentStyles.btnFontFamily || baseFontFamily;
  const btnFontWeight = currentStyles.btnFontWeight || '700';

  const linkColor = currentStyles.linkColor || undefined;

  const showSocialAuth = settings.showSocialAuth !== false;
  const loginTitle = settings.loginTitle || undefined;
  const registerTitle = settings.registerTitle || undefined;
  const resetTitle = settings.resetTitle || undefined;

  const elementOrder: string[] = Array.isArray(settings.element_order) && settings.element_order.length > 0
    ? settings.element_order
    : DEFAULT_AUTH_ELEMENT_ORDER;

  const hiddenElements: string[] = Array.isArray(settings.hidden_elements) ? settings.hidden_elements : [];

  const shadowClass =
    cardBg === 'transparent' || cardShadow === 'none' ? '' :
    cardShadow === 'sm' ? 'shadow-sm' :
    cardShadow === 'md' ? 'shadow-md' :
    cardShadow === 'lg' ? 'shadow-lg' :
    cardShadow === '2xl' ? 'shadow-2xl' :
    'shadow-xl';

  const alignWrapperClass =
    alignment === 'left' ? 'justify-start' :
    alignment === 'right' ? 'justify-end' :
    'justify-center';

  const styleProps = {
    baseFontFamily,
    titleColor,
    titleFontFamily,
    titleFontSize,
    titleFontWeight,
    titleAlign,
    inputBg,
    inputTextColor,
    inputBorderColor,
    inputBorderRadius,
    inputHeight,
    labelColor,
    btnBg,
    btnTextColor,
    btnBorderRadius,
    btnHeight,
    btnFontFamily,
    btnFontWeight,
    linkColor,
    showSocialAuth,
    elementOrder,
    hiddenElements,
  };

  return (
    <div className={cn("flex items-center w-full", alignWrapperClass)}>
      <div
        className={cn("w-full p-5 sm:p-8 border transition-all duration-300", shadowClass)}
        style={{
          backgroundColor: cardBg,
          borderRadius: cardBorderRadius,
          borderColor: cardBorderColor,
          borderWidth: `${cardBorderWidth}px`,
          borderStyle: cardBorderWidth === 0 || cardBg === 'transparent' && cardBorderWidth === 0 ? 'none' : 'solid',
          maxWidth: maxWidth,
          fontFamily: baseFontFamily,
        }}
      >
        {isAdmin ? (
          <AuthBoxAdminPreview
            mode={mode}
            loginTitle={loginTitle}
            registerTitle={registerTitle}
            resetTitle={resetTitle}
            styleProps={styleProps}
          />
        ) : (
          <div key={localMode} className="animate-in fade-in slide-in-from-bottom-2 duration-200">
            {localMode === 'register' && allowRegister ? (
              <RegisterForm
                title={registerTitle}
                styleProps={styleProps}
                onSwitchMode={() => setLocalMode('login')}
              />
            ) : localMode === 'reset' ? (
              <ResetForm
                title={resetTitle}
                styleProps={styleProps}
                onSwitchMode={() => setLocalMode('login')}
              />
            ) : (
              <LoginForm
                title={loginTitle}
                styleProps={styleProps}
                onSwitchMode={allowRegister ? () => setLocalMode('register') : undefined}
                onSwitchToReset={() => setLocalMode('reset')}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AuthBoxAdminPreview({
  mode,
  loginTitle,
  registerTitle,
  resetTitle,
  styleProps,
}: {
  mode: AuthMode;
  loginTitle?: string;
  registerTitle?: string;
  resetTitle?: string;
  styleProps: any;
}) {
  const isRegister = mode === 'register';
  const isReset = mode === 'reset';
  const title = isReset ? (resetTitle || 'Reset Hasła') : isRegister ? (registerTitle || 'Zarejestruj się') : (loginTitle || 'Zaloguj się');
  const submitLabel = isReset ? 'Wyślij kod' : isRegister ? 'Zarejestruj się' : 'Zaloguj się';

  return (
    <div className="pointer-events-none select-none space-y-4" style={{ fontFamily: styleProps.baseFontFamily }}>
      <h2
        className="mb-2 font-extrabold tracking-tight"
        style={{
          color: styleProps.titleColor,
          fontFamily: styleProps.titleFontFamily,
          fontSize: styleProps.titleFontSize,
          fontWeight: styleProps.titleFontWeight,
          textAlign: styleProps.titleAlign,
        }}
      >
        {title}
      </h2>

      {isRegister && (
        <div className="flex justify-center gap-2 bg-gray-100 p-1 rounded-xl">
          <span className="flex-1 text-center rounded-lg px-4 py-2 text-xs font-bold bg-white shadow-xs">Klient Indywidualny</span>
          <span className="flex-1 text-center rounded-lg px-4 py-2 text-xs font-bold text-gray-400">Firma (B2B)</span>
        </div>
      )}

      <div className="space-y-3">
        <div
          className="w-full border rounded-lg bg-white flex items-center px-3 text-xs text-gray-400"
          style={{
            backgroundColor: styleProps.inputBg,
            borderColor: styleProps.inputBorderColor,
            borderRadius: styleProps.inputBorderRadius,
            height: styleProps.inputHeight,
            fontFamily: styleProps.baseFontFamily,
          }}
        >
          Email
        </div>
        {!isReset && (
          <div
            className="w-full border rounded-lg bg-white flex items-center px-3 text-xs text-gray-400"
            style={{
              backgroundColor: styleProps.inputBg,
              borderColor: styleProps.inputBorderColor,
              borderRadius: styleProps.inputBorderRadius,
              height: styleProps.inputHeight,
              fontFamily: styleProps.baseFontFamily,
            }}
          >
            Hasło
          </div>
        )}
        <div
          className="w-full flex items-center justify-center text-xs font-bold shadow-xs transition-opacity"
          style={{
            backgroundColor: styleProps.btnBg || 'var(--primary)',
            color: styleProps.btnTextColor,
            borderRadius: styleProps.btnBorderRadius,
            height: styleProps.btnHeight,
            fontFamily: styleProps.btnFontFamily || styleProps.baseFontFamily,
            fontWeight: styleProps.btnFontWeight,
          }}
        >
          {submitLabel}
        </div>
      </div>

      {styleProps.showSocialAuth && !isReset && (
        <div className="h-10 rounded-xl border border-gray-200 bg-white shadow-2xs flex items-center justify-center text-xs text-gray-700 font-bold gap-2">
          <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
          </svg>
          <span>Zaloguj się przez Google</span>
        </div>
      )}

      <p className="text-center text-[11px] text-gray-400 italic pt-2">
        Podgląd formularza — aktywne działanie na sklepie.
      </p>
    </div>
  );
}

function SubmitButton({
  loading,
  label,
  styleProps,
  disabled,
}: {
  loading: boolean;
  label: string;
  styleProps: any;
  disabled?: boolean;
}) {
  return (
    <button
      className="w-full font-bold hover:opacity-90 focus:outline-none disabled:opacity-50 transition-all shadow-md flex items-center justify-center cursor-pointer"
      style={{
        backgroundColor: styleProps.btnBg || 'var(--primary)',
        color: styleProps.btnTextColor,
        borderRadius: styleProps.btnBorderRadius,
        height: styleProps.btnHeight,
        fontFamily: styleProps.btnFontFamily || styleProps.baseFontFamily || 'inherit',
        fontWeight: styleProps.btnFontWeight,
      }}
      type="submit"
      disabled={disabled || loading}
    >
      {label}
    </button>
  );
}

function LoginForm({
  title,
  styleProps,
  onSwitchMode,
  onSwitchToReset,
}: {
  title?: string;
  styleProps: any;
  onSwitchMode?: () => void;
  onSwitchToReset: () => void;
}) {
  const { t, tPath } = useTranslation();
  const { data: session, status } = useSession();
  // A session whose token could no longer be refreshed still reads as 'authenticated' until it's
  // cleared — sending that user on would only bounce them straight back here.
  const signedIn = status === 'authenticated' && !!session?.user && !(session as any).error;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Staff accounts with two-step login: correct password, now the code (auth/mfa.py).
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);

  useEffect(() => {
    if (!signedIn || !session?.user) return;
    const urlParams = new URLSearchParams(window.location.search);
    const callbackUrl = urlParams.get('callbackUrl');
    let target: string;
    if (callbackUrl && callbackUrl.startsWith('/') && !callbackUrl.startsWith('//')) {
      target = callbackUrl;
    } else if (isStaffRole((session.user as any).role)) {
      // proxy.ts sends each staff role on to its own area of the panel.
      target = '/admin';
    } else {
      target = tPath('/moje-konto');
    }
    // A full page load, not router.replace(): the client router could answer from its cache —
    // e.g. the redirect to this very page it got for the same address before signing in — and
    // the form would vanish with nothing happening. The server also renders the target with the
    // new session cookie from the start.
    window.location.replace(target);
  }, [signedIn, session, tPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password) {
      setError(t('login.error_required_fields', 'Uzupełnij email i hasło.'));
      return;
    }

    setLoading(true);
    try {
      let result;
      try {
        result = await signIn('credentials', { redirect: false, email, password });
      } catch (err) {
        console.warn('Chwilowe zerwanie połączenia HTTP, ponawianie logowania...', err);
        result = await signIn('credentials', { redirect: false, email, password });
      }

      invalidateClientSessionCache();

      const challenge = mfaChallengeFromError(result?.error);
      if (challenge) {
        setMfaChallenge(challenge);
        setPassword('');
      } else if (result?.error === 'MUST_RESET_PASSWORD') {
        setError(t('login.error_must_reset_password', 'Przenieśliśmy nasz sklep na nową platformę — wysłaliśmy link do ustawienia nowego hasła na Twój adres email.'));
      } else if (result?.error) {
        setError(t('login.error_invalid', 'Nieprawidłowy email lub hasło'));
      }
    } catch (err) {
      setError(t('login.error_generic', 'Wystąpił błąd podczas logowania'));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: styleProps.inputBg,
    color: styleProps.inputTextColor,
    borderColor: styleProps.inputBorderColor,
    borderRadius: styleProps.inputBorderRadius,
    height: styleProps.inputHeight,
    fontFamily: styleProps.baseFontFamily || 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    color: styleProps.labelColor,
    fontFamily: styleProps.baseFontFamily || 'inherit',
  };

  const isVisible = (key: string) => !styleProps.hiddenElements?.includes(key);

  const renderElement = (key: string) => {
    if (!isVisible(key)) return null;

    switch (key) {
      case 'title':
        return (
          <h2
            key="title"
            className="mb-6 font-extrabold tracking-tight"
            style={{
              color: styleProps.titleColor,
              fontFamily: styleProps.titleFontFamily || styleProps.baseFontFamily,
              fontSize: styleProps.titleFontSize,
              fontWeight: styleProps.titleFontWeight,
              textAlign: styleProps.titleAlign,
            }}
          >
            {title || t('login.title', 'Zaloguj się')}
          </h2>
        );

      case 'fields':
        return (
          <div key="fields" className="space-y-4 mb-4">
            <div>
              <label
                className="mb-1.5 block text-xs font-bold"
                style={labelStyle}
                htmlFor="auth-box-email"
              >
                {t('login.email', 'Email')}
              </label>
              <input
                className="w-full appearance-none border px-3 py-2 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                id="auth-box-email"
                type="email"
                placeholder={t('login.email', 'Email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label
                className="mb-1.5 block text-xs font-bold"
                style={labelStyle}
                htmlFor="auth-box-password"
              >
                {t('login.password', 'Hasło')}
              </label>
              <div className="relative">
                <input
                  className="w-full appearance-none border px-3 pr-10 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                  id="auth-box-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="******************"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={inputStyle}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hide_password', 'Ukryj hasło') : t('auth.show_password', 'Pokaż hasło')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        );

      case 'forgot_password':
        return (
          <div key="forgot_password" className="flex justify-end mb-4">
            <button
              type="button"
              onClick={onSwitchToReset}
              className="text-xs font-bold hover:underline cursor-pointer"
              style={{ color: styleProps.linkColor || styleProps.btnBg || 'var(--primary)', fontFamily: styleProps.baseFontFamily || 'inherit' }}
            >
              {t('login.forgot_password', 'Zapomniałeś hasła?')}
            </button>
          </div>
        );

      case 'submit_button':
        return (
          <div key="submit_button" className="mb-4">
            <SubmitButton
              loading={loading || signedIn}
              label={signedIn
                ? t('login.redirecting', 'Przekierowuję...')
                : loading ? t('login.submitting', 'Logowanie...') : t('login.submit', 'Zaloguj się')}
              styleProps={styleProps}
            />
          </div>
        );

      case 'social_auth':
        return styleProps.showSocialAuth ? (
          <div key="social_auth" className="mb-4">
            <SocialAuthButtons mode="login" />
          </div>
        ) : null;

      case 'switch_mode':
        return !onSwitchMode ? null : (
          <p key="switch_mode" className="mt-6 text-center text-xs text-gray-600" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
            {t('login.no_account', 'Nie masz konta?')}{' '}
            <button
              type="button"
              onClick={onSwitchMode}
              className="font-bold hover:underline ml-1 cursor-pointer"
              style={{ color: styleProps.linkColor || styleProps.btnBg || 'var(--primary)', fontFamily: styleProps.baseFontFamily || 'inherit' }}
            >
              {t('login.register_here', 'Zarejestruj się')}
            </button>
          </p>
        );

      default:
        return null;
    }
  };

  if (mfaChallenge) {
    return <MfaLoginStep challenge={mfaChallenge} onCancel={() => setMfaChallenge(null)} />;
  }

  return (
    <>
      {error && (
        <div className="mb-4 rounded-xl bg-destructive/10 p-3 text-destructive text-xs font-semibold border border-destructive/20" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {styleProps.elementOrder.map((key: string) => renderElement(key))}
      </form>
    </>
  );
}

function RegisterForm({
  title,
  styleProps,
  onSwitchMode,
}: {
  title?: string;
  styleProps: any;
  onSwitchMode: () => void;
}) {
  const router = useRouter();
  const { t, tPath } = useTranslation();
  const [type, setType] = useState<'b2c' | 'b2b'>('b2c');
  const [formData, setFormData] = useState({
    email: '', password: '', password_confirm: '', company_name: '', nip: '', phone: '', street: '', postal_code: '', city: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // NIP check (Biała Lista MF / VIES) — part of the `b2b` module; without it the form is unchanged.
  const nipCheck = hasModule(useSiteConfig(), 'b2b');
  const [companyCheck, setCompanyCheck] = useState<CompanyCheck | null>(null);
  const [nipError, setNipError] = useState('');
  const [checking, setChecking] = useState(false);
  const checkedFor = useRef('');
  const inFlight = useRef(''); // blur fires right before a click on "Sprawdź" — ask once
  // Values the registry put into the form — replaced when the NIP changes, never what the user typed.
  const autofilled = useRef<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === 'nip') {
      setNipError('');
      if (parseTaxId(e.target.value).number !== parseTaxId(checkedFor.current).number) setCompanyCheck(null);
    }
  };

  const nipErrorText = (raw: string) => {
    const parsed = parseTaxId(raw);
    if (parsed.valid) return '';
    return parsed.kind === 'nip'
      ? t('register.nip_invalid', 'Nieprawidłowy NIP — sprawdź cyfry.')
      : t('register.vat_invalid', 'Nieprawidłowy numer VAT.');
  };

  const checkCompany = async (raw: string = formData.nip) => {
    if (!nipCheck || !raw.trim()) return;
    const invalid = nipErrorText(raw);
    setNipError(invalid);
    if (invalid) { setCompanyCheck(null); return; }
    const parsed = parseTaxId(raw);
    const key = `${parsed.country}${parsed.number}`;
    if ((key === checkedFor.current && companyCheck) || key === inFlight.current) return;
    inFlight.current = key;
    setChecking(true);
    try {
      const res: CompanyCheck = await fetchAPI(`/auth/company-lookup?tax_id=${encodeURIComponent(raw)}`);
      checkedFor.current = key;
      setCompanyCheck(res);
      const fill: Record<string, string | null | undefined> = {
        company_name: res.name, street: res.street, postal_code: res.postal_code, city: res.city,
      };
      setFormData((prev) => {
        const next = { ...prev };
        for (const [field, value] of Object.entries(fill)) {
          const current = (prev as Record<string, string>)[field];
          if (value && (!current.trim() || current === autofilled.current[field])) {
            (next as Record<string, string>)[field] = value;
            autofilled.current[field] = value;
          }
        }
        return next;
      });
    } catch {
      setCompanyCheck({ tax_id: key, country: parsed.country, kind: parsed.kind, status: 'unavailable' });
    } finally {
      inFlight.current = '';
      setChecking(false);
    }
  };

  const checkMessage = (c: CompanyCheck): { text: string; tone: 'ok' | 'warn' } => {
    if (c.status === 'verified') {
      return c.vat_status === 'eu_valid'
        ? { text: t('register.nip_status_eu_valid', 'Numer VAT UE aktywny (VIES).'), tone: 'ok' }
        : { text: t('register.nip_status_active', 'Czynny podatnik VAT — dane firmy uzupełnione z rejestru.'), tone: 'ok' };
    }
    if (c.status === 'warning') {
      return c.vat_status === 'exempt'
        ? { text: t('register.nip_status_exempt', 'Firma zwolniona z VAT. Możesz kontynuować — sklep sprawdzi dane przy akceptacji konta.'), tone: 'warn' }
        : { text: t('register.nip_status_not_vat', 'Firma nie figuruje jako czynny podatnik VAT. Możesz kontynuować — sklep sprawdzi dane przy akceptacji konta.'), tone: 'warn' };
    }
    if (c.status === 'not_found') {
      return { text: t('register.nip_status_not_found', 'Nie znaleźliśmy tego numeru w rejestrach. Sprawdź cyfry — możesz też kontynuować, sklep zweryfikuje dane.'), tone: 'warn' };
    }
    if (c.status === 'unverifiable') {
      return { text: t('register.nip_status_foreign', 'Numer spoza UE — sklep sprawdzi dane ręcznie.'), tone: 'warn' };
    }
    return { text: t('register.nip_status_unavailable', 'Nie udało się teraz sprawdzić numeru. Możesz kontynuować — sklep sprawdzi dane później.'), tone: 'warn' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.email.trim() || !formData.password || !formData.password_confirm) {
      setError(t('register.error_required_fields', 'Uzupełnij wszystkie wymagane pola.'));
      return;
    }
    if (type === 'b2b' && (!formData.company_name.trim() || !formData.nip.trim() || !formData.phone.trim())) {
      setError(t('register.error_required_fields', 'Uzupełnij wszystkie wymagane pola.'));
      return;
    }
    if (type === 'b2b' && nipCheck) {
      const invalid = nipErrorText(formData.nip);
      if (invalid) {
        setNipError(invalid);
        setError(invalid);
        return;
      }
    }
    if (formData.password !== formData.password_confirm) {
      setError(t('register.passwords_mismatch', 'Hasła nie są identyczne'));
      return;
    }

    setLoading(true);
    try {
      const payload: any = { email: formData.email, password: formData.password, password_confirm: formData.password_confirm, type };
      if (type === 'b2b') {
        payload.company_name = formData.company_name;
        payload.nip = formData.nip;
        payload.phone = formData.phone;
        if (nipCheck) {
          payload.street = formData.street;
          payload.postal_code = formData.postal_code;
          payload.city = formData.city;
        }
      }
      await fetchAPI('/auth/register', { method: 'POST', body: JSON.stringify(payload) });
      router.push(tPath(`/weryfikacja?email=${encodeURIComponent(formData.email)}`));
    } catch (err: any) {
      setError(err.message || t('register.error_generic', 'Błąd rejestracji'));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: styleProps.inputBg,
    color: styleProps.inputTextColor,
    borderColor: styleProps.inputBorderColor,
    borderRadius: styleProps.inputBorderRadius,
    height: styleProps.inputHeight,
    fontFamily: styleProps.baseFontFamily || 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    color: styleProps.labelColor,
    fontFamily: styleProps.baseFontFamily || 'inherit',
  };

  const isVisible = (key: string) => !styleProps.hiddenElements?.includes(key);

  const renderElement = (key: string) => {
    if (!isVisible(key)) return null;

    switch (key) {
      case 'title':
        return (
          <h2
            key="title"
            className="mb-6 font-extrabold tracking-tight"
            style={{
              color: styleProps.titleColor,
              fontFamily: styleProps.titleFontFamily || styleProps.baseFontFamily,
              fontSize: styleProps.titleFontSize,
              fontWeight: styleProps.titleFontWeight,
              textAlign: styleProps.titleAlign,
            }}
          >
            {title || t('register.title', 'Zarejestruj się')}
          </h2>
        );

      case 'account_type':
        return (
          <div key="account_type" className="mb-6 flex justify-center gap-2 bg-gray-100 p-1 rounded-xl" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
            <button
              className={cn('flex-1 rounded-lg px-4 py-2 text-xs font-bold transition-all cursor-pointer', type === 'b2c' ? 'bg-white text-primary shadow-xs' : 'text-gray-400 hover:text-gray-900')}
              onClick={() => setType('b2c')}
              type="button"
              style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}
            >
              {t('register.individual_client', 'Klient Indywidualny')}
            </button>
            <button
              className={cn('flex-1 rounded-lg px-4 py-2 text-xs font-bold transition-all cursor-pointer', type === 'b2b' ? 'bg-white text-primary shadow-xs' : 'text-gray-400 hover:text-gray-900')}
              onClick={() => setType('b2b')}
              type="button"
              style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}
            >
              {t('register.company_b2b', 'Firma (B2B)')}
            </button>
          </div>
        );

      case 'fields':
        return (
          <div key="fields" className="space-y-3 mb-4">
            <div>
              <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-reg-email">
                {t('checkout.email', 'Email')}
              </label>
              <input
                className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                id="auth-box-reg-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>

            {type === 'b2b' && (
              <>
                {nipCheck && (
                  <div>
                    <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-nip">
                      {t('register.nip_or_vat', 'NIP / numer VAT UE')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        className="min-w-0 flex-1 appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                        id="auth-box-nip"
                        name="nip"
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        value={formData.nip}
                        onChange={handleChange}
                        onBlur={() => checkCompany()}
                        aria-invalid={!!nipError}
                        aria-describedby="auth-box-nip-hint auth-box-nip-status"
                        style={inputStyle}
                      />
                      <button
                        type="button"
                        onClick={() => checkCompany()}
                        disabled={checking || !formData.nip.trim()}
                        className="shrink-0 border px-3 text-xs font-bold transition-colors hover:border-primary disabled:opacity-50 cursor-pointer"
                        style={{ ...inputStyle, width: 'auto' }}
                      >
                        {checking ? <Loader2 className="h-4 w-4 animate-spin" aria-label={t('register.nip_checking', 'Sprawdzamy numer…')} /> : t('register.nip_check', 'Sprawdź')}
                      </button>
                    </div>
                    <p id="auth-box-nip-hint" className="mt-1 text-[11px] text-gray-500" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
                      {t('register.nip_hint', 'Firmy spoza Polski: numer VAT UE z prefiksem kraju, np. DE123456789.')}
                    </p>
                    <div id="auth-box-nip-status" aria-live="polite" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
                      {nipError ? (
                        <p className="mt-1 text-[11px] font-semibold text-red-600">{nipError}</p>
                      ) : checking ? (
                        <p className="mt-1 text-[11px] text-gray-500">{t('register.nip_checking', 'Sprawdzamy numer…')}</p>
                      ) : companyCheck ? (() => {
                        const msg = checkMessage(companyCheck);
                        return (
                          <p className={cn('mt-1 text-[11px] font-semibold', msg.tone === 'ok' ? 'text-emerald-700' : 'text-amber-700')}>
                            {msg.tone === 'ok' ? '✓ ' : ''}{msg.text}
                          </p>
                        );
                      })() : null}
                    </div>
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-company">
                    {t('register.company_name', 'Nazwa Firmy')}
                  </label>
                  <input
                    className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                    id="auth-box-company"
                    name="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>
                {!nipCheck && (
                  <div>
                    <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-nip">
                      {t('register.nip', 'NIP')}
                    </label>
                    <input
                      className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                      id="auth-box-nip"
                      name="nip"
                      type="text"
                      value={formData.nip}
                      onChange={handleChange}
                      style={inputStyle}
                    />
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-phone">
                    {t('register.phone', 'Telefon kontaktowy')}
                  </label>
                  <input
                    className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                    id="auth-box-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                </div>
                {nipCheck && (
                  <>
                    <div>
                      <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-street">
                        {t('register.street', 'Ulica i numer')}
                      </label>
                      <input
                        className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                        id="auth-box-street"
                        name="street"
                        type="text"
                        autoComplete="address-line1"
                        value={formData.street}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </div>
                    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-postal">
                          {t('register.postal_code', 'Kod pocztowy')}
                        </label>
                        <input
                          className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                          id="auth-box-postal"
                          name="postal_code"
                          type="text"
                          autoComplete="postal-code"
                          value={formData.postal_code}
                          onChange={handleChange}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-city">
                          {t('register.city', 'Miejscowość')}
                        </label>
                        <input
                          className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                          id="auth-box-city"
                          name="city"
                          type="text"
                          autoComplete="address-level2"
                          value={formData.city}
                          onChange={handleChange}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            <div>
              <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-reg-password">
                {t('login.password', 'Hasło')}
              </label>
              <div className="relative">
                <input
                  className="w-full appearance-none border px-3 pr-10 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                  id="auth-box-reg-password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('register.password_hint', 'Min. 8 znaków')}
                  value={formData.password}
                  onChange={handleChange}
                  style={inputStyle}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hide_password', 'Ukryj hasło') : t('auth.show_password', 'Pokaż hasło')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-bold" style={labelStyle} htmlFor="auth-box-reg-password-confirm">
                {t('register.confirm_password', 'Potwierdź Hasło')}
              </label>
              <div className="relative">
                <input
                  className="w-full appearance-none border px-3 pr-10 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
                  id="auth-box-reg-password-confirm"
                  name="password_confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  value={formData.password_confirm}
                  onChange={handleChange}
                  style={inputStyle}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPasswordConfirm((v) => !v)}
                  aria-label={showPasswordConfirm ? t('auth.hide_password', 'Ukryj hasło') : t('auth.show_password', 'Pokaż hasło')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  {showPasswordConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        );

      case 'submit_button':
        return (
          <div key="submit_button" className="mb-4 pt-2">
            <SubmitButton
              loading={loading}
              label={loading ? t('register.submitting', 'Rejestrowanie...') : t('register.submit', 'Zarejestruj się')}
              styleProps={styleProps}
            />
          </div>
        );

      case 'social_auth':
        return styleProps.showSocialAuth ? (
          <div key="social_auth" className="mb-4">
            <SocialAuthButtons mode="register" />
          </div>
        ) : null;

      case 'switch_mode':
        return !onSwitchMode ? null : (
          <p key="switch_mode" className="mt-6 text-center text-xs text-gray-600" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
            {t('register.has_account', 'Masz już konto?')}{' '}
            <button
              type="button"
              onClick={onSwitchMode}
              className="font-bold hover:underline ml-1 cursor-pointer"
              style={{ color: styleProps.linkColor || styleProps.btnBg || 'var(--primary)', fontFamily: styleProps.baseFontFamily || 'inherit' }}
            >
              {t('login.title', 'Zaloguj się')}
            </button>
          </p>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {error && (
        <div className="mb-4 rounded-xl bg-destructive/10 p-3 text-destructive text-xs font-semibold border border-destructive/20" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {styleProps.elementOrder.map((key: string) => renderElement(key))}
      </form>
    </>
  );
}

function ResetForm({
  title,
  styleProps,
  onSwitchMode,
}: {
  title?: string;
  styleProps: any;
  onSwitchMode: () => void;
}) {
  const { t, tPath } = useTranslation();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError(t('reset_password.email_required', 'Podaj adres email.'));
      return;
    }

    setLoading(true);
    try {
      await fetchAPI('/auth/password-reset/request', { method: 'POST', body: JSON.stringify({ email }) });
      setMessage(t('reset_password.success', 'Jeśli konto istnieje, wysłaliśmy kod resetujący na Twój email.'));
    } catch (err) {
      setError(t('reset_password.error', 'Wystąpił błąd. Spróbuj ponownie.'));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: styleProps.inputBg,
    color: styleProps.inputTextColor,
    borderColor: styleProps.inputBorderColor,
    borderRadius: styleProps.inputBorderRadius,
    height: styleProps.inputHeight,
    fontFamily: styleProps.baseFontFamily || 'inherit',
  };

  const labelStyle: React.CSSProperties = {
    color: styleProps.labelColor,
    fontFamily: styleProps.baseFontFamily || 'inherit',
  };

  return (
    <>
      <h2
        className="mb-6 font-extrabold tracking-tight"
        style={{
          color: styleProps.titleColor,
          fontFamily: styleProps.titleFontFamily || styleProps.baseFontFamily,
          fontSize: styleProps.titleFontSize,
          fontWeight: styleProps.titleFontWeight,
          textAlign: styleProps.titleAlign,
        }}
      >
        {title || t('reset_password.title', 'Reset Hasła')}
      </h2>

      {message ? (
        <div className="text-center space-y-4" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
          <div className="rounded-xl bg-green-50 p-4 text-green-700 text-xs font-semibold border border-green-200">
            {message}
          </div>
          <Link
            href={tPath(`/zmien-haslo?email=${encodeURIComponent(email)}`)}
            className="inline-block font-bold text-xs hover:underline"
            style={{ color: styleProps.linkColor || styleProps.btnBg || 'var(--primary)', fontFamily: styleProps.baseFontFamily || 'inherit' }}
          >
            {t('reset_password.enter_code', 'Wpisz kod weryfikacyjny')}
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
          <p className="mb-5 text-xs text-gray-500 leading-relaxed">
            {t('reset_password.instruction', 'Podaj adres email powiązany z kontem, aby otrzymać kod resetujący.')}
          </p>

          {error && (
            <div className="mb-4 rounded-xl bg-destructive/10 p-3 text-destructive text-xs font-semibold border border-destructive/20">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label
              className="mb-1.5 block text-xs font-bold"
              style={labelStyle}
              htmlFor="auth-box-reset-email"
            >
              {t('checkout.email', 'Email')}
            </label>
            <input
              className="w-full appearance-none border px-3 text-xs font-medium shadow-xs focus:border-primary focus:outline-none transition-colors"
              id="auth-box-reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />
          </div>

          <SubmitButton
            loading={loading}
            label={loading ? t('reset_password.sending', 'Wysyłanie...') : t('reset_password.send_code', 'Wyślij kod')}
            styleProps={styleProps}
          />
        </form>
      )}

      <p className="mt-6 text-center text-xs" style={{ fontFamily: styleProps.baseFontFamily || 'inherit' }}>
        <button
          type="button"
          onClick={onSwitchMode}
          className="font-bold hover:underline text-gray-500 cursor-pointer"
          style={{ color: styleProps.linkColor, fontFamily: styleProps.baseFontFamily || 'inherit' }}
        >
          {t('reset_password.back_to_login', 'Wróć do logowania')}
        </button>
      </p>
    </>
  );
}

